/** Turns a placed archetype into a flyable node: MissionParams, sky, modifiers and the reward (03 §6). */

import type { Pcg32 } from '../../sim/rng';
import type { ArchetypeId, Biome, MissionParams, ModifierId } from '../../sim/types';
import { inStorm } from '../../sim/weather';
import { archetype } from '../content/archetypes';
import type { HazardRules } from '../content/career';
import { REWARD } from '../content/economy';
import { MODIFIER_RULES, MODIFIERS, TIME_WEIGHTS, WEATHER } from '../content/modifiers';
import { stampDogTeams } from '../dogs';
import { modifierAllowed, nodeProgress, rewardMultiplier, threatFor } from '../threat';
import type { TheatreNode } from '../types';
import { shuffled, weighted } from '../util';

export interface DressContext {
  difficulty: number;
  readiness: number;
  rules: HazardRules;
  /** Theatres in the operation: how far along a node lies sets its threat cap. */
  theatres: number;
}

/** One more modifier the node can legally carry at this rating (when one is given), or null. */
export function extraModifier(
  rng: Pcg32, id: ArchetypeId, present: readonly ModifierId[], threat?: number,
): ModifierId | null {
  const options = shuffled(rng, MODIFIERS).filter((m) => modifierAllowed(m.id, present, id, threat));
  return options[0]?.id ?? null;
}

function rollModifiers(
  rng: Pcg32, node: TheatreNode, id: ArchetypeId, rules: HazardRules, threat: number,
): ModifierId[] {
  const weights = MODIFIER_RULES.countWeights[Math.min(node.theatre, MODIFIER_RULES.countWeights.length - 1)] ?? [1];
  let count = weighted(rng, weights.map((w, n) => [n, w] as const));
  if (node.keystone && rules.keystoneModifier) count = Math.max(count, 1);
  const modifiers: ModifierId[] = [];
  if (rules.nightOrStorm) {
    // Hazard 10 dresses every mission in one or the other - the other, where the archetype rules one out.
    const first: ModifierId = rng.chance(0.5) ? 'night-op' : 'storm';
    modifiers.push(modifierAllowed(first, [], id) ? first : first === 'storm' ? 'night-op' : 'storm');
  }
  while (modifiers.length < count) {
    const next = extraModifier(rng, id, modifiers, threat);
    if (!next) break;
    modifiers.push(next);
  }
  return modifiers;
}

/** Node reward before multipliers: heavier archetypes, later theatres and keystones pay more. */
export function baseReward(id: ArchetypeId, theatre: number, keystone: boolean, rules: HazardRules): number {
  const rp = REWARD.base + REWARD.perThreat * archetype(id).baseThreat + REWARD.perTheatre * theatre
    + (keystone ? REWARD.keystoneBonus : 0);
  return Math.round(rp * rules.rewardScale);
}

/** Time of day and weather that agree with the modifiers. */
export function skyFor(
  rng: Pcg32, biome: Biome, modifiers: readonly ModifierId[],
): Pick<MissionParams, 'timeOfDay' | 'weather'> {
  return {
    timeOfDay: modifiers.includes('night-op') ? 'night' : weighted(rng, TIME_WEIGHTS),
    weather: modifiers.includes('storm') ? WEATHER[biome].storm : weighted(rng, WEATHER[biome].calm),
  };
}

/** After a modifier is added or removed later in the run: make the sky agree, changing nothing else. */
export function enforceSky(params: MissionParams): void {
  if (params.modifiers.includes('night-op')) params.timeOfDay = 'night';
  if (params.modifiers.includes('storm')) params.weather = WEATHER[params.biome].storm;
  // Weather Window was paid to take the storm away, so a sky that is a storm on its own has to go
  // with it. `inStorm` decides which those are rather than a list written out here: this branch
  // named `blizzard` and `sandstorm` by hand, and a third storm weather would have walked straight
  // through the back door it left open (ROUND5) - on temperate, into a storm the card had bought off.
  else if (inStorm({ weather: params.weather, modifiers: [] })) params.weather = 'clear';
}

/** Fill in a mission, keystone or rescue node. `modifiers` overrides the roll (rescue nodes carry none). */
export function dressNode(
  rng: Pcg32, node: TheatreNode, id: ArchetypeId, biome: Biome, ctx: DressContext, modifiers?: ModifierId[],
): void {
  const progress = nodeProgress(node.theatre, node.column, ctx.theatres);
  const threat = threatFor(id, ctx.difficulty, ctx.readiness, progress);
  const mods = modifiers ?? rollModifiers(rng, node, id, ctx.rules, threat);
  node.variant = archetype(id).native !== biome;
  node.params = {
    archetype: id, biome, ...skyFor(rng, biome, mods), threat, seed: rng.nextU32(), modifiers: mods, intel: [],
  };
  stampDogTeams(node.params, ctx.difficulty);
  node.rewardRp = baseReward(id, node.theatre, node.keystone, ctx.rules);
  node.rewardMultiplier = rewardMultiplier(mods, node.variant);
}
